import React from "react";
import { Badge, Button, Callout, Card, Field, Input, Logo } from "../components/DesignSystem";
import { validateReset, validateSignin } from "../domain/validation";
import type { AuthSession } from "../domain/types";
import { errorMessage } from "../services/errors";
import { usePlannerData } from "../state/PlannerDataContext";
import { ActivationScreen, RequestAccessScreen } from "./AccessScreens";

type AuthView = "sign-in" | "request-access" | "activate" | "forgot" | "reset" | "verify";

function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(() => window.matchMedia("(max-width: 860px)").matches);
  React.useEffect(() => {
    const query = window.matchMedia("(max-width: 860px)");
    const onChange = () => setIsMobile(query.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);
  return isMobile;
}

export function AuthScreens({ onAuthenticated, initialView, presetOrderNumber }: { onAuthenticated: (session: AuthSession) => void; initialView?: AuthView; presetOrderNumber?: string }) {
  const { service, setSession, refresh } = usePlannerData();
  const [view, setView] = React.useState<AuthView>(initialView || "sign-in");
  const [loading, setLoading] = React.useState(false);
  const [serverError, setServerError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const mobile = useIsMobile();

  const wrap = async (operation: () => Promise<AuthSession | void>) => {
    setLoading(true);
    setServerError(null);
    setSuccess(null);
    try {
      const result = await operation();
      if (result) {
        setSession(result);
        await refresh();
        if (!result.verified) setView("verify");
        else onAuthenticated(result);
      }
    } catch (err) {
      setServerError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pob-app" style={{ display: mobile ? "flex" : "grid", flexDirection: mobile ? "column" : undefined, gridTemplateColumns: mobile ? undefined : "minmax(320px, 520px) 1fr", minHeight: "100%", overflowY: "auto", background: "var(--surface-page)" }}>
      <aside style={{ padding: mobile ? "var(--space-5)" : "var(--space-8)", borderRight: mobile ? undefined : "1px solid var(--border-subtle)", borderBottom: mobile ? "1px solid var(--border-subtle)" : undefined, display: "flex", flexDirection: "column", justifyContent: mobile ? "flex-start" : "space-between", gap: mobile ? "var(--space-4)" : "var(--space-8)", flex: mobile ? "0 0 auto" : undefined }}>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
          <Logo height={mobile ? 24 : 30} />
          <span style={{ font: "var(--type-label)" }}>Pockets of Beige</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
          <span className="pob-eyebrow">Wedding planner</span>
          {!mobile && <h1 style={{ font: "var(--type-title)", color: "var(--text-primary)" }}>Pockets of Beige Planner</h1>}
          {!mobile && <p style={{ font: "var(--type-body)", color: "var(--text-secondary)", maxWidth: 360 }}>Sign in, create a wedding, and keep multi-event planning connected across guests, RSVP, and home.</p>}
          <Badge tone="accent">Connected planner</Badge>
        </div>
        {!mobile && <span style={{ font: "var(--type-body-sm)", color: "var(--text-tertiary)" }}>Pockets of Beige</span>}
      </aside>
      <main style={{ display: "flex", alignItems: mobile ? "stretch" : "center", justifyContent: "center", padding: mobile ? "var(--space-5)" : "var(--space-8)", flex: mobile ? "1 1 auto" : undefined }}>
        <Card padding="var(--space-6)" style={{ width: mobile ? "100%" : 460 }}>
          {serverError && <Callout tone="error" title="Server error" style={{ marginBottom: "var(--space-4)" }}>{serverError}</Callout>}
          {success && <Callout tone="success" title="Success" style={{ marginBottom: "var(--space-4)" }}>{success}</Callout>}
          {view === "sign-in" && <SignIn loading={loading} onForgot={() => setView("forgot")} onRequestAccess={() => setView("request-access")} onActivate={() => setView("activate")} onSubmit={(input) => wrap(() => service.auth.signIn(input))} />}
          {view === "request-access" && <RequestAccessScreen onSignIn={() => setView("sign-in")} />}
          {view === "activate" && <ActivationScreen onSignIn={() => setView("sign-in")} onAuthenticated={onAuthenticated} presetOrderNumber={presetOrderNumber} />}
          {view === "forgot" && <Forgot loading={loading} onSignIn={() => setView("sign-in")} onSubmit={(email) => wrap(async () => { await service.auth.requestPasswordReset(email); setSuccess("Reset instructions were sent."); setView("reset"); })} />}
          {view === "reset" && <Reset loading={loading} onSignIn={() => setView("sign-in")} onSubmit={(input) => wrap(async () => { await service.auth.resetPassword(input); setSuccess("Your password has been reset. You can sign in."); setView("sign-in"); })} />}
          {view === "verify" && <Verify loading={loading} onResend={() => wrap(async () => { await service.auth.resendVerification(); setSuccess("Verification email resent."); })} onVerified={(code) => wrap(() => service.auth.verifyEmail(code))} />}
        </Card>
      </main>
    </div>
  );
}

function SignIn({ loading, onForgot, onRequestAccess, onActivate, onSubmit }: { loading: boolean; onForgot: () => void; onRequestAccess: () => void; onActivate: () => void; onSubmit: (input: { email: string; password: string }) => void }) {
  const [input, setInput] = React.useState({ email: "", password: "" });
  const validation = validateSignin(input);
  return (
    <form onSubmit={(event) => { event.preventDefault(); if (validation.valid) onSubmit(input); }} style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <Header eyebrow="Welcome back" title="Sign in" />
      <Field label="Email" error={validation.errors.email}><Input value={input.email} onChange={(e) => setInput({ ...input, email: e.target.value })} invalid={Boolean(validation.errors.email)} /></Field>
      <Field label="Password" error={validation.errors.password}><Input type="password" value={input.password} onChange={(e) => setInput({ ...input, password: e.target.value })} invalid={Boolean(validation.errors.password)} /></Field>
      <Button variant="primary" type="submit" disabled={!validation.valid || loading} fullWidth>{loading ? "Signing in..." : "Sign in"}</Button>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
        <Button variant="secondary" size="sm" onClick={onActivate} fullWidth>Activate my Pockets of Beige account</Button>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "var(--space-3)" }}>
          <Button variant="ghost" size="sm" onClick={onForgot}>Forgot password</Button>
          <Button variant="ghost" size="sm" onClick={onRequestAccess}>Request access</Button>
        </div>
      </div>
    </form>
  );
}

function Forgot({ loading, onSignIn, onSubmit }: { loading: boolean; onSignIn: () => void; onSubmit: (email: string) => void }) {
  const [email, setEmail] = React.useState("");
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  return (
    <form onSubmit={(event) => { event.preventDefault(); if (valid) onSubmit(email); }} style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <Header eyebrow="Account recovery" title="Forgot password" />
      <Field label="Email" error={email && !valid ? "Enter a valid email address." : undefined}><Input value={email} onChange={(e) => setEmail(e.target.value)} invalid={Boolean(email && !valid)} /></Field>
      <Button variant="primary" type="submit" disabled={!valid || loading} fullWidth>{loading ? "Sending..." : "Send reset link"}</Button>
      <Button variant="ghost" size="sm" onClick={onSignIn}>Back to sign in</Button>
    </form>
  );
}

function Reset({ loading, onSignIn, onSubmit }: { loading: boolean; onSignIn: () => void; onSubmit: (input: { token: string; password: string }) => void }) {
  const [input, setInput] = React.useState({ token: "", password: "", confirm: "" });
  const validation = validateReset(input);
  return (
    <form onSubmit={(event) => { event.preventDefault(); if (validation.valid) onSubmit({ token: input.token, password: input.password }); }} style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <Header eyebrow="Reset workflow" title="Choose a new password" />
      <Field label="Reset code" error={validation.errors.token}><Input value={input.token} onChange={(e) => setInput({ ...input, token: e.target.value })} invalid={Boolean(validation.errors.token)} /></Field>
      <Field label="New password" error={validation.errors.password}><Input type="password" value={input.password} onChange={(e) => setInput({ ...input, password: e.target.value })} invalid={Boolean(validation.errors.password)} /></Field>
      <Field label="Confirm password" error={validation.errors.confirm}><Input type="password" value={input.confirm} onChange={(e) => setInput({ ...input, confirm: e.target.value })} invalid={Boolean(validation.errors.confirm)} /></Field>
      <Button variant="primary" type="submit" disabled={!validation.valid || loading} fullWidth>{loading ? "Resetting..." : "Reset password"}</Button>
      <Button variant="ghost" size="sm" onClick={onSignIn}>Back to sign in</Button>
    </form>
  );
}

function Verify({ loading, onResend, onVerified }: { loading: boolean; onResend: () => void; onVerified: (code: string) => void }) {
  const [code, setCode] = React.useState("");
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <Header eyebrow="Email verification" title="Check your email" />
      <Callout tone="neutral" title="Verification required">Confirm the owner email before the planner opens.</Callout>
      <Field label="Verification code"><Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Enter code" /></Field>
      <Button variant="primary" disabled={!code.trim() || loading} onClick={() => onVerified(code)} fullWidth>{loading ? "Verifying..." : "Verify email"}</Button>
      <Button variant="ghost" size="sm" onClick={onResend} disabled={loading}>Resend verification</Button>
    </div>
  );
}

function Header({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", marginBottom: "var(--space-2)" }}>
      <span className="pob-eyebrow">{eyebrow}</span>
      <h2 style={{ font: "var(--type-section)", color: "var(--text-primary)" }}>{title}</h2>
    </div>
  );
}
